// src/components/Chat/MediaAttachmentModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { MEDIA_TYPES } from '../../../store/services/chatService';
import 'bootstrap-icons/font/bootstrap-icons.css';

const MediaAttachmentModal = ({ onSelectMedia, show, onClose, chatBoxId }) => {
  const currentUser = useSelector(state => state.auth.currentUser);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const actionOptions = [
    { 
      id: 'take_photo', 
      name: 'Select Photo', 
      icon: 'bi bi-camera', 
      color: 'text-primary',
      mediaType: MEDIA_TYPES.IMAGE,
      accept: 'image/*',
      capture: 'environment'
    },
    { 
      id: 'take_video', 
      name: 'Select Video', 
      icon: 'bi bi-camera-video', 
      color: 'text-success',
      mediaType: MEDIA_TYPES.VIDEO,
      accept: 'video/*',
      capture: 'environment'
    },
    // { 
    //   id: 'choose_media', 
    //   name: 'Choose Media', 
    //   icon: 'fa-images', 
    //   color: 'text-info',
    //   mediaType: MEDIA_TYPES.IMAGE,
    //   accept: 'image/*,video/*,audio/*'
    // },
    // { 
    //   id: 'record_audio', 
    //   name: 'Record Audio', 
    //   icon: 'fa-microphone', 
    //   color: 'text-warning',
    //   mediaType: MEDIA_TYPES.AUDIO,
    //   accept: 'audio/*',
    //   capture: 'user'
    // },
    { 
      id: 'choose_audio', 
      name: 'Select Audio', 
      icon: 'bi bi-apple-music', 
      color: 'text-purple',
      mediaType: MEDIA_TYPES.AUDIO,
      accept: 'audio/*'
    },
    // { 
    //   id: 'location', 
    //   name: 'Location', 
    //   icon: 'fa-map-marker-alt', 
    //   color: 'text-danger',
    //   mediaType: MEDIA_TYPES.LOCATION
    // },
  ];

  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previews.forEach(preview => {
        if (preview.preview && preview.preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview.preview);
        }
      });
    };
  }, []);

  const handleActionClick = (action) => {
    switch (action.id) {
      case 'take_photo':
        cameraInputRef.current.accept = action.accept;
        cameraInputRef.current.capture = action.capture;
        cameraInputRef.current.click();
        break;
      case 'take_video':
        videoInputRef.current.accept = action.accept;
        videoInputRef.current.capture = action.capture;
        videoInputRef.current.click();
        break;
      case 'choose_media':
        mediaInputRef.current.accept = action.accept;
        mediaInputRef.current.click();
        break;
      case 'record_audio':
        // For audio recording, we'll use the device microphone
        audioInputRef.current.accept = action.accept;
        audioInputRef.current.capture = action.capture;
        audioInputRef.current.click();
        break;
      case 'choose_audio':
        // For selecting existing audio files
        audioInputRef.current.accept = action.accept;
        audioInputRef.current.removeAttribute('capture');
        audioInputRef.current.click();
        break;
      case 'location':
        handleShareLocation(action.mediaType);
        break;
      default:
        break;
    }
  };

  const handleFileSelect = (e, mediaType, isCapture = false) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      const filePreviews = files.map(file => ({
        file,
        type: mediaType,
        preview: URL.createObjectURL(file),
        name: getPreviewName(file, mediaType, isCapture),
        size: file.size,
        isCapture: isCapture,
        duration: null, // We'll get this later for audio/video
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));
      
      // Get duration for audio/video files
      if (mediaType === MEDIA_TYPES.AUDIO || mediaType === MEDIA_TYPES.VIDEO) {
        getMediaDuration(files[0]).then(duration => {
          if (duration) {
            filePreviews[0].duration = duration;
            // setPreviews(prev => [...prev, ...filePreviews]);
          }
        });
      }
      
      // For capture mode, show preview modal
      if (isCapture) {
        setPreviews(filePreviews);
        setSelectedFiles(files);
        setShowPreviewModal(true);
      } else {
        setSelectedFiles(prev => [...prev, ...files]);
        setPreviews(prev => [...prev, ...filePreviews]);
      }
      
      // Clear the file input
      e.target.value = '';
    }
  };

  const getPreviewName = (file, mediaType, isCapture) => {
    if (isCapture) {
      switch (mediaType) {
        case MEDIA_TYPES.IMAGE: return 'Photo Capture';
        case MEDIA_TYPES.VIDEO: return 'Video Recording';
        case MEDIA_TYPES.AUDIO: return 'Audio Recording';
        default: return file.name;
      }
    }
    return file.name;
  };

  const getMediaDuration = (file) => {
    return new Promise((resolve) => {
      if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
        const url = URL.createObjectURL(file);
        const media = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
        
        media.onloadedmetadata = () => {
          resolve(Math.round(media.duration));
          URL.revokeObjectURL(url);
        };
        
        media.onerror = () => {
          resolve(null);
          URL.revokeObjectURL(url);
        };
        
        media.src = url;
        media.load();
      } else {
        resolve(null);
      }
    });
  };

  const handleShareLocation = (mediaType) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            address: null
          };
          
          // Send location immediately
          onSelectMedia([location], [mediaType]);
          onClose();
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please check permissions.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  const handleSend = () => {
    if (previews.length > 0 && onSelectMedia) {
      const filesToSend = [];
      const mediaTypesToSend = [];
      
      previews.forEach(preview => {
        filesToSend.push(preview.file);
        mediaTypesToSend.push(preview.type);
      });
      
      onSelectMedia(filesToSend, mediaTypesToSend);
    }
    
    handleClose();
  };

  const handleClose = () => {
    // Clean up object URLs
    previews.forEach(preview => {
      if (preview.preview && preview.preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview.preview);
      }
    });
    
    setPreviews([]);
    setSelectedFiles([]);
    setShowPreviewModal(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Main Action Sheet Modal */}
      <Modal show={show} onHide={handleClose} centered className="action-sheet-modal">
        <Modal.Body className="p-0">
          <div className="action-sheet-container">
            <div className="action-sheet-header text-center py-3 border-bottom">
              <h6 className="mb-0">Share Media</h6>
            </div>
            
            <div className="action-sheet-options">
              {actionOptions.map((action) => (
                <div 
                  key={action.id}
                  className="action-option d-flex align-items-center p-3 border-bottom"
                  onClick={() => handleActionClick(action)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={`action-icon ${action.color}`}>
                    <i className={`fas ${action.icon} fa-lg`} style={{fontSize: '1.25rem'}}></i>
                  </div>
                  <div className="action-text ms-3">
                    <span className="d-block">{action.name}</span>
                    <small className="text-muted">
                      {action.id === 'record_audio' ? 'Record using microphone' : 
                       action.id === 'choose_audio' ? 'Select audio file' : 
                       action.id === 'take_photo' ? 'Use camera' :
                       action.id === 'take_video' ? 'Record video' :
                       action.id === 'choose_media' ? 'From gallery' :
                       'Share location'}
                    </small>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="action-sheet-footer text-center p-3">
              <button 
                className="btn btn-light w-100"
                onClick={handleClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Preview Modal for Captured/Selected Media */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previews.map((preview, index) => (
            <div key={preview.id || index} className="mb-3">
              {preview.type === MEDIA_TYPES.IMAGE && preview.preview ? (
                <div className="text-center">
                  <img 
                    src={preview.preview} 
                    alt={preview.name}
                    className="img-fluid rounded"
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                  />
                  <p className="mt-2 mb-1">{preview.name}</p>
                  <small className="text-muted">
                    {formatFileSize(preview.size)}
                  </small>
                </div>
              ) : preview.type === MEDIA_TYPES.VIDEO && preview.preview ? (
                <div className="text-center">
                  <video 
                    controls
                    className="rounded w-100"
                    style={{ maxHeight: '300px' }}
                  >
                    <source src={preview.preview} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <p className="mt-2 mb-1">{preview.name}</p>
                  <div className="d-flex justify-content-center text-muted">
                    <small>{formatFileSize(preview.size)}</small>
                    {preview.duration && (
                      <small className="ms-3">Duration: {formatDuration(preview.duration)}</small>
                    )}
                  </div>
                </div>
              ) : preview.type === MEDIA_TYPES.AUDIO ? (
                <div className="audio-preview">
                  <div className="d-flex align-items-center justify-content-center bg-light rounded p-4">
                    <div className="text-center">
                      <i className="fas fa-music fa-3x mb-3" style={{ color: '#6f42c1' }}></i>
                      <p className="mb-1 fw-bold">{preview.name}</p>
                      <div className="mb-3">
                        <audio 
                          controls 
                          className="mt-2"
                          style={{ width: '250px' }}
                        >
                          <source src={preview.preview} type="audio/mpeg" />
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                      <div className="d-flex justify-content-center text-muted">
                        <small>{formatFileSize(preview.size)}</small>
                        {preview.duration && (
                          <small className="ms-3">Duration: {formatDuration(preview.duration)}</small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <button 
            className="btn btn-secondary" 
            onClick={() => {
              setShowPreviewModal(false);
              if (previews.some(p => p.isCapture)) {
                handleClose();
              }
            }}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSend}
          >
            Send
          </button>
        </Modal.Footer>
      </Modal>

      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={cameraInputRef}
        style={{ display: 'none' }}
        capture="environment"
        onChange={(e) => handleFileSelect(e, MEDIA_TYPES.IMAGE, true)}
      />
      
      <input
        type="file"
        ref={videoInputRef}
        style={{ display: 'none' }}
        capture="environment"
        onChange={(e) => handleFileSelect(e, MEDIA_TYPES.VIDEO, true)}
      />
      
      <input
        type="file"
        ref={mediaInputRef}
        style={{ display: 'none' }}
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files);
          files.forEach(file => {
            const mediaType = file.type.startsWith('image/') ? MEDIA_TYPES.IMAGE :
                            file.type.startsWith('video/') ? MEDIA_TYPES.VIDEO :
                            file.type.startsWith('audio/') ? MEDIA_TYPES.AUDIO :
                            MEDIA_TYPES.IMAGE;
            handleFileSelect({ target: { files: [file] } }, mediaType, false);
          });
        }}
      />
      
      <input
        type="file"
        ref={audioInputRef}
        style={{ display: 'none' }}
        accept="audio/*"
        onChange={(e) => handleFileSelect(e, MEDIA_TYPES.AUDIO, true)}
      />
    </>
  );
};

export default MediaAttachmentModal;